import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHandleSignInCallback } from '@logto/react';

import { env } from '@/env';

/**
 * Two-mode callback:
 *
 *  1. **Standalone web**: standard Logto callback — `useHandleSignInCallback`
 *     parses the auth code, completes the PKCE exchange, persists tokens.
 *
 *  2. **External browser bridge** (Telegram flow): the system browser lands
 *     here after Logto. We don't run the SDK callback (its PKCE state lives
 *     in the WebView's localStorage, not the system browser's). Instead we
 *     redirect to a `t.me/<bot>/app?startapp=auth_<code>` deep link so the
 *     mini app reopens with the code accessible via `tgWebAppStartParam`.
 *
 * Detection: we treat any callback that opens *outside* a Telegram WebView as
 * a bridge invocation. `Telegram.WebApp.initData` is empty in the system
 * browser even when navigated from the WebView, so it's a reliable signal.
 */
export function AuthCallback() {
  const isInTelegram =
    !!(window as unknown as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp
      ?.initData;
  return isInTelegram ? <StandardCallback /> : <BridgeCallback />;
}

function StandardCallback() {
  const navigate = useNavigate();
  const { isLoading, error } = useHandleSignInCallback(() => {
    navigate('/', { replace: true });
  });

  useEffect(() => {
    if (error) console.error('[auth] Logto sign-in callback failed:', error);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-bg p-6 text-text">
      <p className="text-fg-muted">
        {isLoading ? 'Signing you in…' : error ? 'Sign-in failed.' : 'Redirecting…'}
      </p>
      {error && (
        <button
          onClick={() => navigate('/', { replace: true })}
          className="rounded-md border border-border px-4 py-2 text-sm"
        >
          Retry
        </button>
      )}
    </div>
  );
}

function BridgeCallback() {
  const code = useMemo(() => new URLSearchParams(window.location.search).get('code'), []);
  const error = useMemo(() => new URLSearchParams(window.location.search).get('error'), []);
  const [autoOpenFailed, setAutoOpenFailed] = useState(false);

  const deepLink = useMemo(() => {
    if (!code) return null;
    // Telegram Main Mini App link format: t.me/<bot>?startapp=<param>
    // (NOT t.me/<bot>/app — that path expects a named Direct Link app
    // registered via /newapp in BotFather, which we haven't created. The
    // wrong path silently opens the bot chat instead of relaunching the
    // mini app, dropping the start_param.)
    return `https://t.me/${env.telegramBotUsername}?startapp=auth_${encodeURIComponent(code)}`;
  }, [code]);

  useEffect(() => {
    if (!deepLink) return;
    // Try auto-redirect; if the browser blocks navigation away, surface a
    // manual button after ~1.5s so the user can tap it.
    window.location.href = deepLink;
    const t = window.setTimeout(() => setAutoOpenFailed(true), 1500);
    return () => window.clearTimeout(t);
  }, [deepLink]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg p-6 text-text">
      <h1 className="text-2xl font-bold text-brand">QLASH</h1>
      {error && (
        <p className="text-center text-sm text-danger">
          Sign-in failed: {error}. Open the Telegram bot and try again.
        </p>
      )}
      {!error && code && (
        <>
          <p className="text-center text-sm text-fg-muted">
            Returning to Telegram…
          </p>
          {autoOpenFailed && deepLink && (
            <a
              href={deepLink}
              className="rounded-md bg-brand px-6 py-3 text-sm font-medium text-brand-fg"
            >
              Open Telegram
            </a>
          )}
        </>
      )}
      {!error && !code && (
        <p className="text-center text-sm text-fg-muted">
          Missing authorization code. Please try signing in again from the bot.
        </p>
      )}
    </div>
  );
}
